import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSideBar } from './SideBarContext';

const SideBarMenuItem = ({
  title,
  icon,
  activeIcon,
  url,
  onClick,
  translate = 'yes',
  badge,
  children,
  variant = 'default',
  tone = 'default',
  disabled = false,
  active,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { setOpenMobile, open } = useSideBar();
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  const showText = open || !isDesktop;

  // `active` lo decide el layout cuando hay varios enlaces a la misma ruta.
  const isActive = active !== undefined
    ? active
    : (url ? location.pathname === url : false);

  const handleClick = (e) => {
    e.preventDefault();
    setOpenMobile(false);
    if (onClick) return onClick();
    if (url) history.push(url);
  };

  // Los ítems del footer son acciones, no navegación: van delineados para no
  // confundirse con los enlaces del menú.
  const isFooter = variant === 'footer';
  const isDanger = tone === 'danger';
  // Acción principal del pie (p. ej. Iniciar sesión): relleno con el rosa de la marca.
  const isAccent = tone === 'accent';

  const footerClasses = isAccent
    ? 'border border-transparent bg-rose-500 text-white font-medium hover:bg-rose-600 active:scale-[0.98]'
    : isDanger
    ? 'border border-rose-200 dark:border-rose-800/60 bg-white dark:bg-gray-900 text-rose-600 dark:text-rose-400 font-medium hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:border-rose-300 dark:hover:border-rose-700 hover:shadow-sm active:scale-[0.98]'
    : 'border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 font-medium hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm active:scale-[0.98]';

  const stateClasses = isFooter
    ? footerClasses
    // Activo: fondo rosa translúcido y texto rosa, como la selección de la barra lateral de iOS.
    : isActive
      ? 'bg-rose-500/10 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400 font-medium'
      : 'text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 active:scale-[0.98]';

  const iconClasses = isFooter
    ? isAccent
      ? 'text-white group-hover:scale-105'
      : isDanger
      ? 'text-rose-500 group-hover:scale-105'
      : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 group-hover:scale-105'
    : isActive
      ? 'text-rose-500'
      // Phosphor Light es de trazo fino: en gris 400 casi no se veía.
      : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 group-hover:scale-105';

  if (disabled) {
    return (
      <li className="list-none">
        <span className="flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm w-full text-gray-400 dark:text-gray-600 italic cursor-not-allowed">
          {icon && <span className="shrink-0">{icon}</span>}
          {/* pr: la cursiva se sale de la caja y truncate cortaba la última letra. */}
          {showText && <span className="truncate pr-1">{title}</span>}
        </span>
      </li>
    );
  }

  return (
    <li className="list-none">
      <a
        href={url || '#'}
        onClick={handleClick}
        className={`group relative flex items-center gap-3 ${isFooter ? 'rounded-full justify-center' : 'rounded-[10px]'} px-3 py-2 text-sm transition-all duration-200 ease-out w-full ${stateClasses}`}
        translate={translate}
      >
        {icon && (
          <span
            className={`shrink-0 transition-all duration-200 ease-out ${iconClasses}`}
          >
            {isActive && activeIcon ? activeIcon : icon}
          </span>
        )}
        {showText && (
          <span className="truncate">
            {title}
          </span>
        )}
        {showText && badge && (
          <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-[0.65rem] font-bold rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-800">
            {badge}
          </span>
        )}
      </a>
      {children && <ul className="ml-8 mt-1 space-y-1">{children}</ul>}
    </li>
  );
};

export default SideBarMenuItem;
